import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client.js';
import type { OrgRole, TrustedIssuer, TrustedIssuerInput } from '../api/client.js';
import { Alert, Badge, Button, Card, Field, Input, Select, Textarea } from './ui/index.js';
import { useT } from '../state/i18n.js';
import './EnterpriseSsoPanel.css';

type VerificationMode = 'jwks' | 'pem';
type LoadState = 'loading' | 'ready' | 'error';

interface FormState {
  issuer: string;
  audience: string;
  mode: VerificationMode;
  jwksUrl: string;
  publicKeyPem: string;
  emailClaim: string;
  defaultRole: 'MEMBER' | 'ADMIN';
  enabled: boolean;
}

const EMPTY_FORM: FormState = {
  issuer: '',
  audience: 'cloudbtl',
  mode: 'jwks',
  jwksUrl: '',
  publicKeyPem: '',
  emailClaim: 'email',
  defaultRole: 'MEMBER',
  enabled: true,
};

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

export function EnterpriseSsoPanel({
  orgId,
  subdomain,
  myRole,
  onError,
}: {
  orgId: string;
  subdomain: string;
  myRole: OrgRole;
  onError: (message: string) => void;
}): JSX.Element | null {
  const t = useT();
  const [state, setState] = useState<LoadState>('loading');
  const [entitled, setEntitled] = useState(false);
  const [issuers, setIssuers] = useState<TrustedIssuer[]>([]);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const loadSequence = useRef(0);

  const load = useCallback(async () => {
    if (myRole !== 'OWNER') return;
    const sequence = ++loadSequence.current;
    setState('loading');
    setLocalError(null);
    try {
      const response = await api.ssoIssuers(orgId);
      if (sequence !== loadSequence.current) return;
      setEntitled(response.entitled);
      setIssuers(response.issuers);
      setState('ready');
    } catch (error) {
      if (sequence !== loadSequence.current) return;
      const message = errorMessage(
        error,
        t({ ko: 'SSO 설정을 불러오지 못했습니다.', en: 'Failed to load SSO settings.' }),
      );
      setLocalError(message);
      setState('error');
      onError(message);
    }
  }, [myRole, onError, orgId]);

  useEffect(() => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    if (myRole === 'OWNER') void load();
    return () => {
      // 빠르게 워크스페이스를 바꿨을 때 이전 응답이 새 org 설정을 덮지 못한다.
      loadSequence.current += 1;
    };
  }, [load, myRole, orgId]);

  if (myRole !== 'OWNER') return null;

  function openCreate(): void {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setLocalError(null);
    setShowForm(true);
  }

  function openEdit(row: TrustedIssuer): void {
    setEditingId(row.id);
    setForm({
      issuer: row.issuer,
      audience: row.audience,
      mode: row.jwksUrl ? 'jwks' : 'pem',
      jwksUrl: row.jwksUrl ?? '',
      publicKeyPem: row.publicKeyPem ?? '',
      emailClaim: row.emailClaim,
      defaultRole: row.defaultRole,
      enabled: !row.disabled,
    });
    setLocalError(null);
    setShowForm(true);
  }

  async function save(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (busyId) return;
    const input: TrustedIssuerInput = {
      issuer: form.issuer.trim(),
      audience: form.audience.trim(),
      // 반대쪽을 빈 문자열로 보내야 verification method 전환 시 기존 값이 제거된다.
      jwksUrl: form.mode === 'jwks' ? form.jwksUrl.trim() : '',
      publicKeyPem: form.mode === 'pem' ? form.publicKeyPem.trim() : '',
      emailClaim: form.emailClaim.trim(),
      defaultRole: form.defaultRole,
      disabled: !form.enabled,
    };
    if (
      !input.issuer ||
      !input.audience ||
      !input.emailClaim ||
      (form.mode === 'jwks' ? !input.jwksUrl : !input.publicKeyPem)
    ) {
      setLocalError(t({ ko: '필수 항목을 모두 입력해 주세요.', en: 'Please fill in all required fields.' }));
      return;
    }
    const operationId = editingId ?? 'create';
    setBusyId(operationId);
    setLocalError(null);
    try {
      if (editingId) await api.updateSsoIssuer(orgId, editingId, input);
      else await api.createSsoIssuer(orgId, input);
      setShowForm(false);
      setEditingId(null);
      await load();
    } catch (error) {
      const message = errorMessage(
        error,
        t({ ko: 'SSO 설정을 저장하지 못했습니다.', en: 'Failed to save SSO settings.' }),
      );
      setLocalError(message);
      onError(message);
    } finally {
      setBusyId(null);
    }
  }

  async function toggle(row: TrustedIssuer): Promise<void> {
    if (busyId || (row.disabled && !entitled)) return;
    setBusyId(row.id);
    setLocalError(null);
    try {
      await api.updateSsoIssuer(orgId, row.id, { disabled: !row.disabled });
      await load();
    } catch (error) {
      const message = errorMessage(
        error,
        t({ ko: 'SSO 상태를 변경하지 못했습니다.', en: 'Failed to change SSO status.' }),
      );
      setLocalError(message);
      onError(message);
    } finally {
      setBusyId(null);
    }
  }

  async function remove(row: TrustedIssuer): Promise<void> {
    if (busyId) return;
    const identityWarning = row.identityCount
      ? t({
          ko: ` 연결된 사용자 매핑 ${row.identityCount}개도 삭제됩니다.`,
          en: ` The ${row.identityCount} linked user mapping(s) will also be deleted.`,
        })
      : '';
    if (
      !window.confirm(
        t({
          ko: `'${row.issuer}' 발급자를 삭제할까요?${identityWarning}`,
          en: `Delete issuer '${row.issuer}'?${identityWarning}`,
        }),
      )
    )
      return;
    setBusyId(row.id);
    setLocalError(null);
    try {
      await api.deleteSsoIssuer(orgId, row.id);
      if (editingId === row.id) {
        setShowForm(false);
        setEditingId(null);
      }
      await load();
    } catch (error) {
      const message = errorMessage(
        error,
        t({ ko: 'SSO 발급자를 삭제하지 못했습니다.', en: 'Failed to delete SSO issuer.' }),
      );
      setLocalError(message);
      onError(message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Card
      tone="muted"
      padding="sm"
      className="sso-panel"
      role="region"
      aria-labelledby="enterprise-sso-title"
      data-testid="enterprise-sso-panel"
    >
      <div className="sso-panel__head">
        <div>
          <h3 id="enterprise-sso-title" className="sso-panel__title">
            {t({ ko: '엔터프라이즈 SSO', en: 'Enterprise SSO' })}
          </h3>
          <p className="sso-panel__dim">
            {t({
              ko: '서명 JWT 발급자를 신뢰하고 사용자를 워크스페이스에 자동 등록합니다.',
              en: 'Trust a signed JWT issuer and auto-enroll users into the workspace.',
            })}
          </p>
        </div>
        {state === 'ready' && entitled && !showForm ? (
          <Button size="sm" variant="primary" onClick={openCreate}>
            {t({ ko: '발급자 추가', en: 'Add issuer' })}
          </Button>
        ) : null}
      </div>

      <Alert tone="info" className="sso-panel__notice">
        {t({
          ko: '현재 방식은 브라우저 리디렉션형 OIDC 로그인이 아니라, 고객 IdP 또는 인증 프록시가 10분 이하 단명 JWT를 API 요청의 Bearer 토큰으로 전달하는 연동입니다. 기존 이메일·Google 로그인은 계속 사용할 수 있습니다.',
          en: "This method isn't browser-redirect OIDC login — it's an integration where the customer's IdP or auth proxy passes a short-lived JWT (10 minutes or less) as the Bearer token on API requests. Existing email/Google login continues to work.",
        })}
      </Alert>

      {state === 'loading' ? (
        <p className="sso-panel__dim" role="status">
          {t({ ko: 'SSO 설정 불러오는 중…', en: 'Loading SSO settings…' })}
        </p>
      ) : null}
      {state === 'error' ? (
        <Button size="sm" onClick={() => void load()}>
          {t({ ko: '다시 시도', en: 'Retry' })}
        </Button>
      ) : null}

      {state === 'ready' && !entitled ? (
        <Alert tone="warning" data-testid="sso-upgrade-message">
          {t({
            ko: 'Enterprise SSO는 Business 플랜 또는 활성 Business 체험에 포함됩니다.',
            en: 'Enterprise SSO is included with the Business plan or an active Business trial.',
          })}{' '}
          <Link to="/plans">{t({ ko: '플랜 보기', en: 'View plans' })}</Link>
        </Alert>
      ) : null}

      {localError ? (
        <Alert tone="danger" role="alert">
          {localError}
        </Alert>
      ) : null}

      {state === 'ready' && issuers.length === 0 && !showForm ? (
        <p className="sso-panel__dim">
          {t({ ko: '등록된 SSO 발급자가 없습니다.', en: 'No SSO issuers registered.' })}
        </p>
      ) : null}

      {state === 'ready' && issuers.length > 0 ? (
        <ul className="sso-panel__list">
          {issuers.map((row) => {
            const active = entitled && !row.disabled;
            const status = row.disabled
              ? t({ ko: '비활성', en: 'Disabled' })
              : entitled
                ? t({ ko: '활성', en: 'Enabled' })
                : t({ ko: '플랜 비활성', en: 'Plan inactive' });
            return (
              <li key={row.id} className="sso-panel__row" data-testid="sso-issuer-row">
                <div className="sso-panel__row-main">
                  <div className="sso-panel__row-title">
                    <code className="sso-panel__issuer">{row.issuer}</code>
                    <Badge tone={active ? 'success' : 'neutral'}>{status}</Badge>
                  </div>
                  <p className="sso-panel__meta">
                    {t({
                      ko: `Audience: ${row.audience} · ${row.jwksUrl ? 'JWKS URL' : 'RSA 공개키'} · 기본 역할 ${row.defaultRole} · 연결 사용자 ${row.identityCount}명`,
                      en: `Audience: ${row.audience} · ${row.jwksUrl ? 'JWKS URL' : 'RSA public key'} · Default role ${row.defaultRole} · ${row.identityCount} connected users`,
                    })}
                  </p>
                </div>
                <div className="sso-panel__actions">
                  {entitled ? (
                    <Button size="sm" disabled={Boolean(busyId)} onClick={() => openEdit(row)}>
                      {t({ ko: '편집', en: 'Edit' })}
                    </Button>
                  ) : null}
                  <Button
                    size="sm"
                    disabled={Boolean(busyId) || (row.disabled && !entitled)}
                    onClick={() => void toggle(row)}
                  >
                    {row.disabled
                      ? t({ ko: '활성화', en: 'Enable' })
                      : t({ ko: '비활성화', en: 'Disable' })}
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={Boolean(busyId)}
                    onClick={() => void remove(row)}
                  >
                    {t({ ko: '삭제', en: 'Delete' })}
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}

      {showForm ? (
        <form className="sso-panel__form" onSubmit={save} data-testid="sso-issuer-form">
          <h4 className="sso-panel__form-title">
            {editingId
              ? t({ ko: 'SSO 발급자 편집', en: 'Edit SSO issuer' })
              : t({ ko: 'SSO 발급자 추가', en: 'Add SSO issuer' })}
          </h4>
          <Field
            label={t({ ko: 'Issuer URL (JWT iss와 정확히 일치)', en: 'Issuer URL (must exactly match JWT iss)' })}
            htmlFor="sso-issuer-url"
            required
          >
            <Input
              id="sso-issuer-url"
              aria-label="Issuer URL"
              type="url"
              required
              value={form.issuer}
              onChange={(event) => setForm((old) => ({ ...old, issuer: event.target.value }))}
              placeholder="https://idp.example.com"
            />
          </Field>
          <Field label="Audience (JWT aud)" htmlFor="sso-audience" required>
            <Input
              id="sso-audience"
              aria-label="Audience"
              required
              value={form.audience}
              onChange={(event) => setForm((old) => ({ ...old, audience: event.target.value }))}
            />
          </Field>
          <Field label={t({ ko: '서명 검증 방식', en: 'Signature verification method' })} htmlFor="sso-verification-mode">
            <Select
              id="sso-verification-mode"
              aria-label={t({ ko: '서명 검증 방식', en: 'Signature verification method' })}
              value={form.mode}
              onChange={(event) =>
                setForm((old) => ({ ...old, mode: event.target.value as VerificationMode }))
              }
            >
              <option value="jwks">
                {t({ ko: 'JWKS URL (키 로테이션 권장)', en: 'JWKS URL (recommended for key rotation)' })}
              </option>
              <option value="pem">{t({ ko: 'RSA 공개키 PEM', en: 'RSA public key PEM' })}</option>
            </Select>
          </Field>
          {form.mode === 'jwks' ? (
            <Field label="JWKS URL" htmlFor="sso-jwks-url" required>
              <Input
                id="sso-jwks-url"
                aria-label="JWKS URL"
                type="url"
                required
                value={form.jwksUrl}
                onChange={(event) => setForm((old) => ({ ...old, jwksUrl: event.target.value }))}
                placeholder="https://idp.example.com/.well-known/jwks.json"
              />
            </Field>
          ) : (
            <Field
              label={t({ ko: 'RSA 공개키 (SPKI PEM)', en: 'RSA public key (SPKI PEM)' })}
              htmlFor="sso-public-key"
              hint={t({ ko: '개인키는 절대 입력하지 마세요.', en: 'Never enter a private key here.' })}
              required
            >
              <Textarea
                id="sso-public-key"
                className="sso-panel__pem"
                aria-label={t({ ko: 'RSA 공개키', en: 'RSA public key' })}
                required
                value={form.publicKeyPem}
                onChange={(event) =>
                  setForm((old) => ({ ...old, publicKeyPem: event.target.value }))
                }
                placeholder="-----BEGIN PUBLIC KEY-----"
              />
            </Field>
          )}
          <div className="sso-panel__grid">
            <Field label={t({ ko: '이메일 claim', en: 'Email claim' })} htmlFor="sso-email-claim" required>
              <Input
                id="sso-email-claim"
                aria-label={t({ ko: '이메일 claim', en: 'Email claim' })}
                required
                value={form.emailClaim}
                onChange={(event) => setForm((old) => ({ ...old, emailClaim: event.target.value }))}
              />
            </Field>
            <Field label={t({ ko: '최초 기본 역할', en: 'Initial default role' })} htmlFor="sso-default-role">
              <Select
                id="sso-default-role"
                aria-label={t({ ko: '최초 기본 역할', en: 'Initial default role' })}
                value={form.defaultRole}
                onChange={(event) =>
                  setForm((old) => ({
                    ...old,
                    defaultRole: event.target.value as 'MEMBER' | 'ADMIN',
                  }))
                }
              >
                <option value="MEMBER">MEMBER</option>
                <option value="ADMIN">ADMIN</option>
              </Select>
            </Field>
          </div>
          <label className="ds-check">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(event) => setForm((old) => ({ ...old, enabled: event.target.checked }))}
            />
            {t({ ko: '저장 즉시 활성화', en: 'Enable immediately on save' })}
          </label>
          <div className="sso-panel__actions">
            <Button type="submit" size="sm" variant="primary" loading={Boolean(busyId)}>
              {busyId ? t({ ko: '저장 중…', en: 'Saving…' }) : t({ ko: '저장', en: 'Save' })}
            </Button>
            <Button size="sm" disabled={Boolean(busyId)} onClick={() => setShowForm(false)}>
              {t({ ko: '취소', en: 'Cancel' })}
            </Button>
          </div>
        </form>
      ) : null}

      <details className="sso-panel__guide">
        <summary>{t({ ko: '연동에 필요한 JWT 규칙', en: 'JWT rules required for integration' })}</summary>
        <ul>
          <li>
            {t({ ko: '요청 주소:', en: 'Request URL:' })}{' '}
            <code>https://{subdomain}.cloudbtl.com/api/…</code>
          </li>
          <li>
            {t({ ko: '헤더:', en: 'Header:' })} <code>Authorization: Bearer &lt;assertion&gt;</code>
          </li>
          <li>
            {t({ ko: '필수 claim:', en: 'Required claims:' })} <code>iss</code>, <code>aud</code>,{' '}
            {t({ ko: '안정적인', en: 'a stable' })} <code>sub</code>,{' '}
            {t({ ko: '요청마다 고유한', en: 'a per-request unique' })} <code>jti</code>,{' '}
            {t({ ko: '이메일 claim', en: 'the email claim' })}, <code>exp</code>
          </li>
          <li>
            {t({
              ko: '만료는 발급 시점부터 최대 10분이며 RS256 비대칭 서명을 사용합니다.',
              en: 'Expiry is at most 10 minutes from issuance, using RS256 asymmetric signing.',
            })}
          </li>
        </ul>
      </details>
    </Card>
  );
}
